/**
 * Newton's law of universal gravitation and the field strength it implies.
 *
 *   F = G m₁ m₂ / r²          force between two point masses (or spheres) r apart
 *   g = G M / (R + h)²        field strength at altitude h above a body of radius R
 *
 * Everything here is SI: kilograms, metres, newtons, m/s². The widget converts units on
 * the way in and out.
 */

import { GRAVITATIONAL_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";

export type GravityError =
  | "non-positive"
  | "not-finite"
  | "below-surface"
  | "missing";

export type Outcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: GravityError };

export type ForceTarget = "force" | "m1" | "m2" | "distance";

export interface ForceInputs {
  force: number | null;
  m1: number | null;
  m2: number | null;
  distance: number | null;
}

export interface ForceSolution {
  force: number;
  m1: number;
  m2: number;
  distance: number;
  /** Acceleration of m₁ toward m₂, m/s². */
  a1: number;
  /** Acceleration of m₂ toward m₁, m/s². */
  a2: number;
}

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/** Solve F = G m₁ m₂ / r² for the chosen unknown; the other three must be positive. */
export function solveGravitationalForce(
  inputs: ForceInputs,
  target: ForceTarget
): Outcome<ForceSolution> {
  const names: ForceTarget[] = ["force", "m1", "m2", "distance"];
  for (const name of names) {
    if (name !== target && inputs[name] === null) {
      return { ok: false, error: "missing" };
    }
    if (name !== target && !positive(inputs[name])) {
      return { ok: false, error: "non-positive" };
    }
  }
  const { force, m1, m2, distance } = inputs;
  const G = GRAVITATIONAL_CONSTANT;
  let solved: ForceSolution | null = null;
  if (
    target === "force" &&
    positive(m1) &&
    positive(m2) &&
    positive(distance)
  ) {
    solved = build((G * m1 * m2) / distance ** 2, m1, m2, distance);
  } else if (
    target === "m1" &&
    positive(force) &&
    positive(m2) &&
    positive(distance)
  ) {
    solved = build(force, (force * distance ** 2) / (G * m2), m2, distance);
  } else if (
    target === "m2" &&
    positive(force) &&
    positive(m1) &&
    positive(distance)
  ) {
    solved = build(force, m1, (force * distance ** 2) / (G * m1), distance);
  } else if (
    target === "distance" &&
    positive(force) &&
    positive(m1) &&
    positive(m2)
  ) {
    solved = build(force, m1, m2, Math.sqrt((G * m1 * m2) / force));
  }
  if (solved === null || !Object.values(solved).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solved };
}

function build(
  force: number,
  m1: number,
  m2: number,
  distance: number
): ForceSolution {
  return { force, m1, m2, distance, a1: force / m1, a2: force / m2 };
}

export type FieldTarget = "gravity" | "mass" | "altitude";

export interface FieldInputs {
  /** Mass of the attracting body, kg. */
  mass: number | null;
  /** Its radius, m. */
  radius: number | null;
  /** Height above the surface, m (zero or more). */
  altitude: number | null;
  /** Field strength g, m/s². */
  gravity: number | null;
}

export interface FieldSolution {
  mass: number;
  radius: number;
  altitude: number;
  /** Distance from the body's centre, R + h. */
  distance: number;
  gravity: number;
  /** g at the surface of the same body, m/s². */
  surfaceGravity: number;
}

function fieldSolution(
  mass: number,
  radius: number,
  altitude: number,
  gravity: number
): FieldSolution {
  const G = GRAVITATIONAL_CONSTANT;
  return {
    mass,
    radius,
    altitude,
    distance: radius + altitude,
    gravity,
    surfaceGravity: (G * mass) / radius ** 2,
  };
}

const FIELD_NEEDS: Record<FieldTarget, readonly (keyof FieldInputs)[]> = {
  gravity: ["mass", "radius", "altitude"],
  mass: ["gravity", "radius", "altitude"],
  altitude: ["mass", "gravity", "radius"],
};

/** Solve g = G M / (R + h)² for g, for M, or for the altitude h. */
export function solveGravitationalField(
  inputs: FieldInputs,
  target: FieldTarget
): Outcome<FieldSolution> {
  const G = GRAVITATIONAL_CONSTANT;
  for (const key of FIELD_NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
    const value = inputs[key];
    const valid =
      key === "altitude" ? value !== null && value >= 0 : positive(value);
    if (!valid) {
      return { ok: false, error: "non-positive" };
    }
  }
  const mass = inputs.mass ?? 0;
  const radius = inputs.radius ?? 0;
  const altitude = inputs.altitude ?? 0;
  const gravity = inputs.gravity ?? 0;
  let solved: FieldSolution;
  if (target === "gravity") {
    solved = fieldSolution(
      mass,
      radius,
      altitude,
      (G * mass) / (radius + altitude) ** 2
    );
  } else if (target === "mass") {
    solved = fieldSolution(
      (gravity * (radius + altitude) ** 2) / G,
      radius,
      altitude,
      gravity
    );
  } else {
    const distance = Math.sqrt((G * mass) / gravity);
    if (distance < radius) {
      return { ok: false, error: "below-surface" };
    }
    solved = fieldSolution(mass, radius, distance - radius, gravity);
  }
  if (!Object.values(solved).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solved };
}
